import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { FiSearch, FiRefreshCw, FiUsers, FiPlus, FiTrash2 } from 'react-icons/fi';
import { getAllTenants, Tenant } from '../api/tenantApi';
import {
    getObjectTypes,
    updateObjectTypeFlags,
    getUserFields,
    updateConversationParticipants,
    EDITABLE_FLAGS,
    RESERVED_PARTICIPANT_ROLE,
    MAX_PARTICIPANTS,
    ObjectType,
    ObjectTypeFlag,
    ConversationParticipant,
    UserField,
} from '../api/objectTypesApi';
import { addNotification } from '../store/slices/uiSlice';

// Extract a human-readable error from any shape the server might return.
function extractError(e: any): string {
    const body = e?.response?.data;
    if (body?.message) return body.message;
    if (typeof body?.error === 'string') return body.error;
    if (body?.error?.message) return body.error.message;
    return e?.message ?? 'Unknown error';
}

// ---------------------------------------------------------------------------
// Conversation participants editor
// ---------------------------------------------------------------------------

/** Editable row — same shape as the API entry, plus a stable key for React. */
interface ParticipantRow extends ConversationParticipant {
    key: number;
}

/** Client-side mirror of the BFF validation so the admin sees the problem before the round trip. */
function validateParticipants(rows: ParticipantRow[]): string | null {
    if (rows.length > MAX_PARTICIPANTS) return `At most ${MAX_PARTICIPANTS} participants are allowed.`;
    const seen = new Set<string>();
    for (let i = 0; i < rows.length; i++) {
        const path = rows[i].path.trim();
        if (!path) return `Row ${i + 1}: select a field.`;
        if (seen.has(path)) return `Row ${i + 1}: "${path}" is listed twice.`;
        seen.add(path);
        if ((rows[i].role ?? '').trim().toLowerCase() === RESERVED_PARTICIPANT_ROLE.toLowerCase()) {
            return `Row ${i + 1}: the role "${RESERVED_PARTICIPANT_ROLE}" is reserved for external institution contacts.`;
        }
    }
    return null;
}

interface ConversationParticipantsModalProps {
    tenantId: string;
    objectType: ObjectType;
    onClose: () => void;
    /** Called with the saved list after a successful PUT. */
    onSaved: (participants: ConversationParticipant[]) => void;
}

const inputClass =
    'block w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100';

const ConversationParticipantsModal: React.FC<ConversationParticipantsModalProps> = ({ tenantId, objectType, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const [rows, setRows] = useState<ParticipantRow[]>(() =>
        (objectType.conversationParticipants ?? []).map((p, i) => ({ ...p, key: i }))
    );
    const [nextKey, setNextKey] = useState((objectType.conversationParticipants ?? []).length);
    const [fields, setFields] = useState<UserField[] | null>(null);
    const [fieldsError, setFieldsError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getUserFields(tenantId, objectType.type)
            .then(r => {
                if (cancelled) return;
                if (!r.success) { setFieldsError(r.message ?? 'Failed to load user fields'); setFields([]); return; }
                setFields(r.data ?? []);
            })
            .catch(e => { if (!cancelled) { setFieldsError(extractError(e)); setFields([]); } });
        return () => { cancelled = true; };
    }, [tenantId, objectType.type]);

    // ESC closes, like ModalContainer.
    useEffect(() => {
        const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape' && !saving) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, saving]);

    const updateRow = (key: number, patch: Partial<ConversationParticipant>) =>
        setRows(rs => rs.map(r => r.key === key ? { ...r, ...patch } : r));

    const addRow = () => {
        setRows(rs => [...rs, { key: nextKey, path: '' }]);
        setNextKey(k => k + 1);
    };

    const removeRow = (key: number) => setRows(rs => rs.filter(r => r.key !== key));

    const save = async () => {
        const problem = validateParticipants(rows);
        if (problem) { setError(problem); return; }
        setError(null);
        setSaving(true);
        const participants: ConversationParticipant[] = rows.map(r => {
            const role = (r.role ?? '').trim();
            const p: ConversationParticipant = { path: r.path.trim() };
            if (role) p.role = role;
            if (r.canManage) p.canManage = true;
            return p;
        });
        try {
            const r = await updateConversationParticipants(tenantId, objectType.type, participants);
            if (!r.success) throw new Error(r.message ?? 'Update failed');
            dispatch(addNotification({
                type: 'success',
                message: `${objectType.label ?? objectType.type}: ${participants.length} conversation participant${participants.length === 1 ? '' : 's'} saved`,
            }));
            onSaved(participants);
        } catch (e: any) {
            setError(extractError(e));
        } finally {
            setSaving(false);
        }
    };

    const layoutFields = (fields ?? []).filter(f => !f.isSystem);
    const systemFields = (fields ?? []).filter(f => f.isSystem);
    const knownPaths = new Set((fields ?? []).map(f => f.path));

    return (
        <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
            onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}
        >
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4" role="dialog" aria-modal="true">
                <div className="p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Conversation participants — {objectType.label ?? objectType.type}
                    </h3>
                    <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
                        Users referenced by these fields are added to every conversation thread on documents of this type.
                        Changes apply to threads created from now on.
                    </p>

                    {fieldsError && (
                        <div className="mb-3 px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
                            Could not load the field list ({fieldsError}). Paths can still be typed by hand.
                        </div>
                    )}

                    {rows.length === 0 ? (
                        <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded">
                            No participants configured. Saving an empty list clears any existing configuration.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Field</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-44">Role</th>
                                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">Can manage</th>
                                        <th className="px-3 py-2 w-10" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {rows.map((row, i) => (
                                        <tr key={row.key}>
                                            <td className="px-3 py-2">
                                                {fields === null ? (
                                                    <div className="text-xs text-gray-400">Loading fields…</div>
                                                ) : fields.length === 0 ? (
                                                    <input
                                                        type="text"
                                                        value={row.path}
                                                        onChange={e => updateRow(row.key, { path: e.target.value })}
                                                        placeholder="summary.section.field or system.createUser"
                                                        aria-label={`Row ${i + 1} field path`}
                                                        className={`${inputClass} font-mono`}
                                                    />
                                                ) : (
                                                    <select
                                                        value={row.path}
                                                        onChange={e => updateRow(row.key, { path: e.target.value })}
                                                        aria-label={`Row ${i + 1} field`}
                                                        className={inputClass}
                                                    >
                                                        <option value="">— Select a field —</option>
                                                        {row.path && !knownPaths.has(row.path) && (
                                                            <option value={row.path}>{row.path} (not in current layout)</option>
                                                        )}
                                                        {layoutFields.length > 0 && (
                                                            <optgroup label="Layout fields">
                                                                {layoutFields.map(f => <option key={f.path} value={f.path}>{f.label}</option>)}
                                                            </optgroup>
                                                        )}
                                                        {systemFields.length > 0 && (
                                                            <optgroup label="System fields">
                                                                {systemFields.map(f => <option key={f.path} value={f.path}>{f.label}</option>)}
                                                            </optgroup>
                                                        )}
                                                    </select>
                                                )}
                                                {row.path && <div className="mt-0.5 text-xs text-gray-400 font-mono">{row.path}</div>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="text"
                                                    value={row.role ?? ''}
                                                    onChange={e => updateRow(row.key, { role: e.target.value })}
                                                    placeholder="Member"
                                                    maxLength={100}
                                                    aria-label={`Row ${i + 1} role`}
                                                    className={inputClass}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={row.canManage === true}
                                                    onChange={e => updateRow(row.key, { canManage: e.target.checked })}
                                                    aria-label={`Row ${i + 1} can manage`}
                                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => removeRow(row.key)}
                                                    title="Remove"
                                                    aria-label={`Remove row ${i + 1}`}
                                                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="mt-3">
                        <button
                            type="button"
                            onClick={addRow}
                            disabled={rows.length >= MAX_PARTICIPANTS}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                            <FiPlus /> Add row
                        </button>
                    </div>

                    {error && (
                        <div className="mt-4 px-3 py-2 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
                            {error}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ObjectTypesPage: React.FC = () => {
    const dispatch = useDispatch();
    const [params, setParams] = useSearchParams();

    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [selectedTenantId, setSelectedTenantId] = useState(params.get('tenantId') ?? '');
    const [objectTypes, setObjectTypes] = useState<ObjectType[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [showSystem, setShowSystem] = useState(false);
    // key = `${type}:${flag}` while that individual toggle is in flight
    const [saving, setSaving] = useState<Record<string, boolean>>({});
    // Object type whose conversation participants are being edited (modal open), if any.
    const [participantsFor, setParticipantsFor] = useState<ObjectType | null>(null);

    useEffect(() => {
        getAllTenants()
            .then(r => setTenants((r as any).data ?? []))
            .catch(() => {});
    }, []);

    const load = useCallback(async (tenantId: string) => {
        if (!tenantId) { setObjectTypes([]); return; }
        setLoading(true);
        setLoadError(null);
        try {
            const r = await getObjectTypes(tenantId);
            if (!r.success) { setLoadError(r.message ?? 'Failed to load object types'); return; }
            const list = r.data ?? [];
            setObjectTypes([...list].sort((a, b) =>
                (a.menuposition ?? 999) - (b.menuposition ?? 999) ||
                (a.label ?? a.type).localeCompare(b.label ?? b.type)
            ));
        } catch (e: any) {
            setLoadError(extractError(e));
            setObjectTypes([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(selectedTenantId); }, [selectedTenantId, load]);

    const onSelectTenant = (tenantId: string) => {
        setSelectedTenantId(tenantId);
        if (tenantId) setParams({ tenantId }); else setParams({});
    };

    const toggle = async (objectType: ObjectType, flag: ObjectTypeFlag, value: boolean) => {
        const key = `${objectType.type}:${flag}`;
        setSaving(s => ({ ...s, [key]: true }));
        // Optimistic — reverted below if the call fails.
        setObjectTypes(list => list.map(o => o.type === objectType.type ? { ...o, [flag]: value } : o));
        try {
            const r = await updateObjectTypeFlags(selectedTenantId, objectType.type, { [flag]: value });
            if (!r.success) throw new Error(r.message ?? 'Update failed');
            dispatch(addNotification({
                type: 'success',
                message: `${objectType.label ?? objectType.type}: ${flag} set to ${value}`,
            }));
        } catch (e: any) {
            setObjectTypes(list => list.map(o => o.type === objectType.type ? { ...o, [flag]: !value } : o));
            dispatch(addNotification({
                type: 'error',
                title: 'Could not update flag',
                message: extractError(e),
            }));
        } finally {
            setSaving(s => { const n = { ...s }; delete n[key]; return n; });
        }
    };

    const onParticipantsSaved = (objectType: ObjectType, participants: ConversationParticipant[]) => {
        // Reflect immediately, then reload so the row matches what AppDefinition stored.
        setObjectTypes(list => list.map(o => o.type === objectType.type ? { ...o, conversationParticipants: participants } : o));
        setParticipantsFor(null);
        load(selectedTenantId);
    };

    const visible = objectTypes
        .filter(o => showSystem || o.systemObject !== true)
        .filter(o => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            return o.type.toLowerCase().includes(q) || (o.label ?? '').toLowerCase().includes(q);
        });

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Object Types</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Per-tenant object definition flags. Field lists come from layouts and are not editable here.
                </p>
            </div>

            {/* Tenant selector + filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-5">
                <div className="flex flex-wrap items-center gap-4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">Tenant</label>
                    <select
                        value={selectedTenantId}
                        onChange={e => onSelectTenant(e.target.value)}
                        className="flex-1 min-w-[16rem] max-w-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                        <option value="">— Select a tenant —</option>
                        {tenants.map(t => (
                            <option key={t._id} value={t.tenantId}>{t.name ?? t.tenantId} ({t.tenantId})</option>
                        ))}
                    </select>

                    {selectedTenantId && (
                        <>
                            <div className="relative">
                                <FiSearch className="absolute left-2.5 top-2.5 text-gray-400" />
                                <input
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Search object types"
                                    className="pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                <input type="checkbox" checked={showSystem} onChange={e => setShowSystem(e.target.checked)} />
                                Show system objects
                            </label>
                            <button
                                onClick={() => load(selectedTenantId)}
                                disabled={loading}
                                className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
                            </button>
                        </>
                    )}
                </div>
            </div>

            {loadError && (
                <div className="mb-5 px-4 py-3 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
                    {loadError}
                </div>
            )}

            {!selectedTenantId ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
                    Select a tenant to view its object types.
                </div>
            ) : loading ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500" />
                </div>
            ) : visible.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
                    No object types to show.
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Object type</th>
                                {EDITABLE_FLAGS.map(f => (
                                    <th key={f.key} title={f.hint} className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                        {f.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {visible.map(o => (
                                <tr key={o.type} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">{o.label ?? o.type}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                            {o.type}
                                            {o.systemObject && <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-200">system</span>}
                                        </div>
                                    </td>
                                    {EDITABLE_FLAGS.map(f => {
                                        const key = `${o.type}:${f.key}`;
                                        const participantCount = o.conversationParticipants?.length ?? 0;
                                        return (
                                            <td key={f.key} className="px-3 py-2.5 text-center whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    checked={o[f.key] === true}
                                                    disabled={!!saving[key]}
                                                    onChange={e => toggle(o, f.key, e.target.checked)}
                                                    aria-label={`${o.label ?? o.type} — ${f.label}`}
                                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40"
                                                />
                                                {f.key === 'hasConversations' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setParticipantsFor(o)}
                                                        disabled={o.hasConversations !== true}
                                                        title={o.hasConversations === true
                                                            ? 'Edit conversation participants'
                                                            : 'Enable Conversations first'}
                                                        aria-label={`${o.label ?? o.type} — conversation participants`}
                                                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed align-middle"
                                                    >
                                                        <FiUsers />
                                                        Participants
                                                        {participantCount > 0 && (
                                                            <span className="px-1 rounded bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200">{participantCount}</span>
                                                        )}
                                                    </button>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {participantsFor && selectedTenantId && (
                <ConversationParticipantsModal
                    key={`${selectedTenantId}:${participantsFor.type}`}
                    tenantId={selectedTenantId}
                    objectType={participantsFor}
                    onClose={() => setParticipantsFor(null)}
                    onSaved={participants => onParticipantsSaved(participantsFor, participants)}
                />
            )}
        </div>
    );
};

export default ObjectTypesPage;
